<script lang="ts" setup>
import BaseDropdownItemContent from "@/components/Base/Dropdown/Item/ItemContent.vue";
import { type Props as BaseIconProps } from "@/components/Base/Icon/Icon.vue";
import { type BootstrapIconName } from "@/types/bootstrap-icons";
import { RouterLink, type RouteLocationRaw } from "vue-router";

defineOptions({ name: "BaseDropdownItem", inheritAttrs: false });

export type Props = {
  disabled?: boolean;
  to?: RouteLocationRaw;
  href?: string;
  iconName?: `${BootstrapIconName}`;
  iconSvg?: Component;
  iconColor?: BaseIconProps["color"];
  iconBgColor?: string;
  label?: string;
  value?: string | number;
  withRadio?: boolean;
  withRadioCheck?: boolean;
  checked?: boolean;
};

const props = defineProps<Props>();

// `inheritAttrs: false` binds `role`/`aria-checked` to the inner control, so an
// item in a `role="menu"` list leaves this `<li>` with its implicit `listitem`
// role in between — axe flags `aria-required-children` on the menu and
// `aria-required-parent` on every item. Neutralise the `<li>` whenever the
// control itself is a menu item, whether via `withRadio` or a passed-through
// `role`.
const MENU_ITEM_ROLES: string[] = [
  "menuitem",
  "menuitemradio",
  "menuitemcheckbox",
];

const attrs = useAttrs();

const listRole = computed<"none" | undefined>(() => {
  if (props.withRadio) return "none";

  const role: unknown = attrs.role;

  return typeof role === "string" && MENU_ITEM_ROLES.includes(role)
    ? "none"
    : undefined;
});
</script>

<template>
  <li :class="{ disabled }" class="base-dropdown-item" :role="listRole">
    <RouterLink
      v-if="to"
      :to="to"
      class="dropdown-item base-dropdown-item__button"
      :class="{
        'with-radio': withRadio,
        'with-radio-check': withRadioCheck,
        disabled,
      }"
      :aria-disabled="disabled || undefined"
      :role="withRadio ? 'menuitemradio' : undefined"
      :aria-checked="withRadio ? checked : undefined"
      v-bind="$attrs"
    >
      <BaseDropdownItemContent
        :icon-name="iconName"
        :icon-svg="iconSvg"
        :icon-color="iconColor"
        :icon-bg-color="iconBgColor"
        :label="label"
        :with-radio="withRadio"
        :with-radio-check="withRadioCheck"
        :checked="checked"
      >
        <slot></slot>
      </BaseDropdownItemContent>
    </RouterLink>
    <a
      v-else-if="href"
      :href="href"
      class="dropdown-item base-dropdown-item__button"
      :class="{
        'with-radio': withRadio,
        'with-radio-check': withRadioCheck,
        disabled,
      }"
      :aria-disabled="disabled || undefined"
      :role="withRadio ? 'menuitemradio' : undefined"
      :aria-checked="withRadio ? checked : undefined"
      v-bind="$attrs"
    >
      <BaseDropdownItemContent
        :icon-name="iconName"
        :icon-svg="iconSvg"
        :icon-color="iconColor"
        :icon-bg-color="iconBgColor"
        :label="label"
        :with-radio="withRadio"
        :with-radio-check="withRadioCheck"
        :checked="checked"
      >
        <slot></slot>
      </BaseDropdownItemContent>
    </a>
    <button
      v-else
      type="button"
      class="dropdown-item base-dropdown-item__button"
      :class="{
        'with-radio': withRadio,
        'with-radio-check': withRadioCheck,
        disabled,
      }"
      :disabled="disabled"
      :aria-disabled="disabled || undefined"
      :value="value"
      :role="withRadio ? 'menuitemradio' : undefined"
      :aria-checked="withRadio ? checked : undefined"
      v-bind="$attrs"
    >
      <BaseDropdownItemContent
        :icon-name="iconName"
        :icon-svg="iconSvg"
        :icon-color="iconColor"
        :icon-bg-color="iconBgColor"
        :label="label"
        :with-radio="withRadio"
        :with-radio-check="withRadioCheck"
        :checked="checked"
      >
        <slot></slot>
      </BaseDropdownItemContent>
    </button>
  </li>
</template>
<style scoped lang="scss">
.base-dropdown-item {
  list-style: none;

  &__button.dropdown-item {
    width: 100%;
    justify-content: start;
    display: flex;
    align-items: center;
    gap: $spacer-2;
    font-size: $font-size-sm;
    line-height: 1.65;
    color: $color-text;
    white-space: nowrap;

    // Reset native <button> chrome so the row sits on Bootstrap's
    // `.dropdown-item` styling instead of user-agent defaults.
    border: 0;
    background-color: transparent;
    text-align: left;
    text-decoration: none;

    --bs-dropdown-link-hover-bg: var(--hover-color);
    --bs-dropdown-link-active-bg: var(--hover-color-dark);
    --bs-dropdown-link-active-color: var(--color-black);

    &.with-radio {
      padding-left: $spacer-3;
      padding-right: $spacer-3;
    }

    &__description {
      font-size: 0.9rem;
      font-weight: 400;
      white-space: normal;
      color: $color-surface-gray-500;
    }

    &--lg {
      display: flex;
      align-items: flex-start;
      gap: $spacer-2;
    }

    &--selected {
      position: relative;

      &::before {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 2px;
        background-color: $primary;
      }
    }

    // Reapply Bootstrap's hover/focus/active backgrounds explicitly — the
    // scoped `background-color: transparent` above sits at equal specificity
    // to Bootstrap's `.dropdown-item:hover` and was winning by source order.
    // Focus paints only on `:focus-visible`, not `:focus` (#13085): a
    // teleported menu moves focus to its first item on open, so a plain
    // `:focus` lit that item up after every mouse click, reading as a
    // preselection. The browser's focus-visible heuristic keeps the highlight
    // for keyboard users — a menu opened with Enter/Space, or an item reached
    // with Tab, still shows where focus is. The transparent background above
    // outranks Bootstrap's own `.dropdown-item:focus`, so that rule stays off.
    &:hover,
    &:focus-visible {
      background-color: var(--bs-dropdown-link-hover-bg);
    }

    &:active,
    &.active {
      background-color: var(--bs-dropdown-link-active-bg);
      color: var(--bs-dropdown-link-active-color);
    }

    &.text-danger:active {
      --bs-danger-rgb: 255, 255, 255;

      background-color: $color-system-error;
    }

    // The `color` above outranks Bootstrap's `.dropdown-item:disabled` rule,
    // so a disabled entry looked enabled (#13079). Grey the label and the
    // icon, which inherits; a description keeps its own readable colour, as it
    // is usually what explains why the entry is disabled. Last in the block so
    // the hover and active rules above cannot light a disabled entry up.
    &.disabled,
    &:disabled {
      color: $color-surface-gray-500;
      background-color: transparent;
    }
  }
}

.disabled {
  cursor: not-allowed;
}
</style>
